import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { changePassword } from "../../services/profileService";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import SettingsField from "./SettingsField";

interface PasswordChangeFormProps {
	/** The signed-in address, pre-filled into the reset dialog. */
	email: string;
	/**
	 * Whether the account carries a usable password hash. This is NOT "did they
	 * sign up with a password": fastapi-users gives OAuth accounts a RANDOM
	 * password, so a Google-only account reads as `true` here — which is why
	 * the hint below points at password reset for anyone who never set one.
	 */
	hasPassword: boolean;
	/** Swap in the replacement token the server mints (see `changePassword`). */
	onPasswordChanged: (accessToken: string) => void;
}

/** Matches the minimum enforced on `/reset-password`. */
const MIN_PASSWORD_LENGTH = 8;

const INPUT_CLASSES =
	"w-full px-3 py-2 border border-gray-300 rounded-lg text-base text-text-base " +
	"focus:outline-none focus:ring-2 focus:ring-[#008782] focus:border-transparent " +
	"disabled:bg-gray-50 disabled:text-text-muted";

/**
 * Styled as the link it replaces. It is a button because it opens a dialog
 * rather than going anywhere -- the navigation was the complaint.
 */
const RESET_LINK_CLASSES = "text-[#008782] hover:underline";

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
	email,
	hasPassword,
	onPasswordChanged,
}) => {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);
	const [resetOpen, setResetOpen] = useState(false);

	const resetDialog = resetOpen ? (
		<ForgotPasswordDialog
			initialEmail={email}
			onClose={() => setResetOpen(false)}
		/>
	) : null;

	if (!hasPassword) {
		return (
			<>
				<p className="text-sm text-text-muted">
					This account has no password set. Use{" "}
					<button
						type="button"
						onClick={() => setResetOpen(true)}
						className={RESET_LINK_CLASSES}
					>
						Forgot password
					</button>{" "}
					to set one.
				</p>
				{resetDialog}
			</>
		);
	}

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setError(null);
		setDone(false);

		if (newPassword.length < MIN_PASSWORD_LENGTH) {
			setError(
				`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
			);
			return;
		}
		if (newPassword !== confirmPassword) {
			setError("New passwords do not match.");
			return;
		}

		setSaving(true);
		try {
			const { access_token } = await changePassword(
				currentPassword,
				newPassword,
			);
			// The token this tab authenticated with was just revoked by the
			// change itself; without swapping it in, the next request 401s and
			// the user is bounced to login for succeeding.
			onPasswordChanged(access_token);
			// Clear on success: leaving the old password sitting in a filled-in
			// field is both wrong and a thing to shoulder-surf.
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
			setDone(true);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Could not change your password.",
			);
		} finally {
			setSaving(false);
		}
	};

	const canSubmit =
		!saving && !!currentPassword && !!newPassword && !!confirmPassword;

	// The dialog sits OUTSIDE this form: it has a form of its own, and a form
	// nested inside another is invalid HTML that browsers silently unnest.
	return (
		<>
		<form onSubmit={handleSubmit} className="space-y-4 max-w-md">
			<SettingsField label="Current password" htmlFor="settings-current-password">
				<input
					id="settings-current-password"
					type="password"
					value={currentPassword}
					onChange={(event) => setCurrentPassword(event.target.value)}
					disabled={saving}
					autoComplete="current-password"
					className={INPUT_CLASSES}
				/>
			</SettingsField>

			<SettingsField label="New password" htmlFor="settings-new-password">
				<input
					id="settings-new-password"
					type="password"
					value={newPassword}
					onChange={(event) => setNewPassword(event.target.value)}
					disabled={saving}
					autoComplete="new-password"
					className={INPUT_CLASSES}
				/>
			</SettingsField>

			<SettingsField
				label="Confirm new password"
				htmlFor="settings-confirm-password"
			>
				<input
					id="settings-confirm-password"
					type="password"
					value={confirmPassword}
					onChange={(event) => setConfirmPassword(event.target.value)}
					disabled={saving}
					autoComplete="new-password"
					className={INPUT_CLASSES}
				/>
			</SettingsField>

			{error && <p className="text-sm text-red-600">{error}</p>}
			{done && (
				<p className="text-sm text-[#008782]">
					Password updated. Other signed-in sessions have been logged out.
				</p>
			)}

			<p className="text-xs text-text-muted">
				Signed in with Google and never set a password? Use{" "}
				<button
					type="button"
					onClick={() => setResetOpen(true)}
					className={RESET_LINK_CLASSES}
				>
					Forgot password
				</button>{" "}
				instead.
			</p>

			<button
				type="submit"
				disabled={!canSubmit}
				className="px-4 py-2 bg-[#008782] text-white rounded-lg text-sm font-medium hover:bg-[#004C4B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
			>
				{saving && <Loader2 className="w-4 h-4 animate-spin" />}
				<span>Change password</span>
			</button>
		</form>
		{resetDialog}
		</>
	);
};

export default PasswordChangeForm;
